import React from "react";
import { getContentRequest } from "duck/api/utils";
import { useChatContext } from "duck/ui/chat/contexts/useChatContext";
import {
  AssistantMessageContent,
  DownloadableContent,
  MessageType,
} from "duck/ui/chat/types";
import { useTranslation } from "react-i18next";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { downloadBlob } from "shared/utils";

import Button from "features/components/Button";

import { Renderer } from "./types";

const ALLOWED_MEDIA_TYPES = ["text/csv", "image/png"];

const getFileExtension = (mediaType: string): string => {
  const extensionMap: Record<string, string> = {
    "text/plain": "txt",
    "image/jpeg": "jpg",
    "application/vnd.ms-excel": "xlsx",
  };

  return (
    extensionMap[mediaType.split(";")[0]] ||
    mediaType.split("/")[1]?.split(";")[0] ||
    "txt"
  );
};

const validateMediaType = (mediaType: string): boolean =>
  ALLOWED_MEDIA_TYPES.includes(mediaType);

const DownloadButton = ({
  downloadable_content,
}: {
  downloadable_content: any;
}) => {
  const { t } = useTranslation("duck");
  const title = downloadable_content.title || t("download");

  const { isStreaming } = useChatContext();

  const disabled =
    isStreaming || !validateMediaType(downloadable_content.media_type);

  const handleDownload = async () => {
    const blob = await getContentRequest(
      { url: downloadable_content.url },
      downloadable_content.media_type
    );
    downloadBlob(
      blob,
      `${title}.${getFileExtension(downloadable_content.media_type)}`
    );
  };

  return (
    <Button
      className="!my-2"
      label={title}
      role="link"
      color="primary"
      startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
      onClick={handleDownload}
      disabled={disabled}
    />
  );
};

const hasDownloadableContent = (content: AssistantMessageContent): boolean =>
  content.message_type === MessageType.DOWNLOADABLE_CONTENT &&
  "url" in content &&
  "media_type" in content &&
  Boolean(content.url) &&
  Boolean(content.media_type);

export const DownloadableContentRenderer: Renderer<AssistantMessageContent> = {
  type: "downloadable",

  canRender: (content: AssistantMessageContent): boolean =>
    hasDownloadableContent(content),

  render: (content: AssistantMessageContent): React.ReactNode => {
    if (!hasDownloadableContent(content)) return null;

    const downloadable_content = content as DownloadableContent;

    return <DownloadButton downloadable_content={downloadable_content} />;
  },
};
